(function () {
  const SIZE_NAME_PATTERN = /beden|size|ölçü|olcu|numara/i;
  const SIZE_VALUE_PATTERN = /^(?:xxxs|xxs|xs|s|m|l|xl|xxl|xxxl|[2-6]xs|[2-6]xl|\d{2,3})$/i;
  const APPAREL_PATTERN = /tişört|tisort|t-shirt|gömlek|gomlek|shirt|pantolon|pants|jean/i;
  const DEFAULT_APPAREL_SIZES = ['S', 'M', 'L', 'XL'];

  function optionNames(product) {
    return (product.options || []).map((option, index) => {
      if (typeof option === 'string') return option;
      return option?.name || `Seçenek ${index + 1}`;
    });
  }

  function variantOptions(variant) {
    if (Array.isArray(variant?.options)) return variant.options;
    return [variant?.option1, variant?.option2, variant?.option3].filter((value) => value != null);
  }

  function findSizeOptionIndex(product) {
    const names = optionNames(product);
    const namedIndex = names.findIndex((name) => SIZE_NAME_PATTERN.test(name));
    if (namedIndex !== -1) return namedIndex;

    const variants = product.variants || [];
    for (let index = 0; index < names.length; index += 1) {
      const values = [...new Set(variants.map((variant) => variantOptions(variant)[index]).filter(Boolean))];
      if (values.length > 1 && values.every((value) => SIZE_VALUE_PATTERN.test(String(value).trim()))) return index;
    }

    return -1;
  }

  function availableVariantForSize(product, sizeIndex, sizeValue, targetColor) {
    const colorNeedle = targetColor ? String(targetColor).trim().toLowerCase() : '';
    if (colorNeedle) {
      const matching = (product.variants || []).find((variant) => {
        if (!variant.available) return false;
        if (String(variantOptions(variant)[sizeIndex]) !== String(sizeValue)) return false;
        const titleStr = String(variant.title || '').toLowerCase();
        const optionsArr = (variant.options || []).map((o) => String(o).toLowerCase());
        return titleStr.includes(colorNeedle) || optionsArr.includes(colorNeedle);
      });
      if (matching) return matching;
    }
    return (product.variants || []).find(
      (variant) => variant.available && String(variantOptions(variant)[sizeIndex]) === String(sizeValue)
    );
  }

  function uniqueSizeValues(product, sizeIndex, targetColor) {
    const seen = new Set();
    const values = [];
    (product.variants || []).forEach((variant) => {
      const value = variantOptions(variant)[sizeIndex];
      if (value == null || seen.has(String(value))) return;
      seen.add(String(value));
      values.push({
        value: String(value),
        available: Boolean(availableVariantForSize(product, sizeIndex, value, targetColor)),
      });
    });
    return values;
  }

  function isApparel(product) {
    const identity = [product?.type, product?.title, product?.handle].filter(Boolean).join(' ');
    return APPAREL_PATTERN.test(identity);
  }

  function sizeSelectionForProduct(product, targetColor) {
    const sizeIndex = findSizeOptionIndex(product);
    if (sizeIndex !== -1) {
      return {
        index: sizeIndex,
        virtual: false,
        values: uniqueSizeValues(product, sizeIndex, targetColor),
      };
    }

    if (!isApparel(product)) return null;

    return {
      index: -1,
      virtual: true,
      values: DEFAULT_APPAREL_SIZES.map((value) => ({ value, available: true })),
    };
  }

  const helpers = {
    optionNames,
    variantOptions,
    findSizeOptionIndex,
    availableVariantForSize,
    uniqueSizeValues,
    isApparel,
    sizeSelectionForProduct,
  };

  if (typeof window !== 'undefined') window.LlagoxQuickCart = helpers;
  if (typeof document === 'undefined') return;

  let initialized = false;
  let modal;
  let dialog;
  let pickerView;
  let successView;
  let errorView;
  let activeTrigger;
  let state = {};

  function init() {
    if (initialized) return;
    modal = document.getElementById('LlagoxQuickCartModal');
    if (!modal) return;
    initialized = true;

    dialog = modal.querySelector('.llagox-quick-cart__dialog');
    pickerView = modal.querySelector('[data-llagox-picker-view]');
    successView = modal.querySelector('[data-llagox-success-view]');
    errorView = modal.querySelector('[data-llagox-error-view]');

    document.addEventListener('click', handleDocumentClick);
    document.addEventListener('keydown', handleKeydown);
    modal.querySelector('[data-llagox-picker-add]')?.addEventListener('click', addSelectedVariant);
  }

  function handleDocumentClick(event) {
    const trigger = event.target.closest('.js-llagox-quick-add');
    if (trigger) {
      event.preventDefault();
      event.stopPropagation();
      if (!trigger.disabled) prepareProduct(trigger);
      return;
    }

    if (event.target.closest('[data-llagox-modal-close]')) closeModal();

    const sizeButton = event.target.closest('[data-llagox-size-value]');
    if (sizeButton && !sizeButton.disabled) selectSize(sizeButton.dataset.llagoxSizeValue);
  }

  function handleKeydown(event) {
    if (modal?.hidden) return;
    if (event.key === 'Escape') {
      closeModal();
      return;
    }
    if (event.key !== 'Tab') return;

    const focusable = [...dialog.querySelectorAll('button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])')]
      .filter((element) => !element.closest('[hidden]'));
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  async function prepareProduct(trigger) {
    activeTrigger = trigger;
    setTriggerLoading(trigger, true);
    try {
      let product = null;
      if (trigger.dataset.llagoxProductJson) {
        try {
          const parsed = JSON.parse(trigger.dataset.llagoxProductJson);
          if (parsed && typeof parsed === 'object' && Array.isArray(parsed.variants) && parsed.variants.length > 0) {
            product = parsed;
          }
        } catch (e) {}
      }
      if (!product && trigger.dataset.llagoxProductUrl) {
        product = await fetchProduct(trigger.dataset.llagoxProductUrl);
      }
      if (!product || typeof product !== 'object') {
        throw new Error('Ürün bilgileri alınamadı.');
      }
      const availableVariants = (product.variants || []).filter((variant) => variant.available);
      if (!availableVariants.length) throw new Error('Bu ürün şu anda stokta bulunmuyor.');

      const sizeSelection = sizeSelectionForProduct(product, trigger.dataset.llagoxSelectedColor);
      if (sizeSelection?.values.length) {
        showSizePicker(product, sizeSelection, trigger);
      } else {
        await addToCart(product, availableVariants[0], trigger);
      }
    } catch (error) {
      showError(error?.message || 'Lütfen kısa bir süre sonra tekrar deneyin.');
    } finally {
      setTriggerLoading(trigger, false);
    }
  }

  async function fetchProduct(rawUrl) {
    const url = new URL(rawUrl, window.location.origin);
    url.hash = '';
    url.search = '';
    url.pathname = `${url.pathname.replace(/\/$/, '')}.js`;
    const response = await fetch(url.toString(), { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Ürün bilgileri alınamadı.');
    return response.json();
  }

  function showSizePicker(product, sizeSelection, trigger) {
    let initialVariant = null;
    if (trigger?.dataset?.llagoxVariantId) {
      const targetId = Number(trigger.dataset.llagoxVariantId);
      initialVariant = (product.variants || []).find((v) => v.id === targetId);
    }
    if (!initialVariant && trigger?.dataset?.llagoxSelectedColor) {
      const colorNeedle = trigger.dataset.llagoxSelectedColor.trim().toLowerCase();
      initialVariant = (product.variants || []).find((v) => {
        const titleStr = String(v.title || '').toLowerCase();
        const optionsArr = (v.options || []).map((o) => String(o).toLowerCase());
        return titleStr.includes(colorNeedle) || optionsArr.includes(colorNeedle);
      });
    }
    if (!initialVariant) {
      initialVariant = (product.variants || []).find((v) => v.available) || (product.variants || [])[0];
    }

    state = {
      product,
      sizeIndex: sizeSelection.index,
      virtualSize: sizeSelection.virtual,
      baseVariant: initialVariant,
      variant: null,
      selectedSize: null,
      properties: {},
      trigger,
    };
    showView(pickerView);

    const image = pickerView.querySelector('[data-llagox-picker-image]');
    const fallbackImage = trigger?.dataset?.llagoxProductImage || imageUrl(initialVariant?.featured_image) || imageUrl(product.featured_image) || '';
    image.src = fallbackImage;
    image.alt = product.title || trigger.dataset.llagoxProductTitle || '';
    pickerView.querySelector('[data-llagox-picker-title]').textContent = product.title || trigger.dataset.llagoxProductTitle || '';
    pickerView.querySelector('[data-llagox-picker-price]').textContent = formatPrice(initialVariant?.price || product.price) || trigger.dataset.llagoxProductPrice || '';
    pickerView.querySelector('[data-llagox-size-label]').textContent =
      (sizeSelection.index !== -1 && optionNames(product)[sizeSelection.index]) || 'Beden';
    pickerView.querySelector('[data-llagox-picker-message]').textContent = '';

    const sizeList = pickerView.querySelector('[data-llagox-size-list]');
    sizeList.replaceChildren(
      ...sizeSelection.values.map(({ value, available }) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'llagox-quick-cart__size';
        button.dataset.llagoxSizeValue = value;
        button.textContent = value;
        button.setAttribute('aria-pressed', 'false');
        button.disabled = !available;
        if (!available) button.setAttribute('aria-label', `${value} bedeni tükendi`);
        return button;
      })
    );

    const addButton = pickerView.querySelector('[data-llagox-picker-add]');
    addButton.disabled = true;
    addButton.textContent = 'BEDEN SEÇİN';
    openModal();
  }

  function selectSize(sizeValue) {
    let variant = null;
    if (state.virtualSize) {
      variant = state.baseVariant || (state.product.variants || []).find((item) => item.available) || (state.product.variants || [])[0];
    } else {
      const targetColor = state.trigger?.dataset?.llagoxSelectedColor || '';
      variant = availableVariantForSize(state.product, state.sizeIndex, sizeValue, targetColor);
    }
    if (!variant) return;
    state.variant = variant;
    state.selectedSize = String(sizeValue);
    state.properties = state.virtualSize ? { Beden: String(sizeValue) } : {};
    if (state.trigger?.dataset?.llagoxProductImage) {
      state.properties._image = state.trigger.dataset.llagoxProductImage;
    }

    pickerView.querySelectorAll('[data-llagox-size-value]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.llagoxSizeValue === String(sizeValue)));
    });

    const fallbackImage = state.trigger?.dataset?.llagoxProductImage || '';
    const variantImage = imageUrl(variant.featured_image) || fallbackImage;
    if (variantImage) pickerView.querySelector('[data-llagox-picker-image]').src = variantImage;
    pickerView.querySelector('[data-llagox-picker-price]').textContent = formatPrice(variant.price);
    pickerView.querySelector('[data-llagox-picker-message]').textContent = `${sizeValue} bedeni seçildi.`;
    const addButton = pickerView.querySelector('[data-llagox-picker-add]');
    addButton.disabled = false;
    addButton.textContent = 'SEPETE EKLE';
  }

  async function addSelectedVariant() {
    if (!state.product || !state.variant) return;
    const button = pickerView.querySelector('[data-llagox-picker-add]');
    button.disabled = true;
    button.textContent = 'EKLENİYOR…';
    try {
      await addToCart(state.product, state.variant, state.trigger, state.properties);
    } catch (error) {
      pickerView.querySelector('[data-llagox-picker-message]').textContent =
        error?.message || 'Ürün sepete eklenemedi. Lütfen tekrar deneyin.';
      button.disabled = false;
      button.textContent = 'SEPETE EKLE';
    }
  }

  async function addToCart(product, variant, trigger, properties = {}) {
    const addUrl = `${(window.routes?.cart_add_url || '/cart/add').replace(/\.js$/, '')}.js`;
    const formData = new FormData();
    formData.append('id', String(variant.id));
    formData.append('quantity', '1');
    const finalProperties = { ...properties };
    if (trigger?.dataset?.llagoxProductImage && !finalProperties._image) {
      finalProperties._image = trigger.dataset.llagoxProductImage;
    }
    Object.entries(finalProperties).forEach(([name, value]) => {
      if (name && value != null && value !== '') {
        formData.append(`properties[${name}]`, String(value));
      }
    });

    const response = await fetch(addUrl, {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/javascript, application/json',
      },
      body: formData,
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(result.description || result.message || 'Ürün sepete eklenemedi.');

    let cart = null;
    try {
      cart = await refreshCartCount();
    } catch (error) {
      // The item is already in Shopify's cart; a badge refresh failure must not invite a duplicate add.
    }
    document.dispatchEvent(
      new CustomEvent('llagox:cart-updated', { detail: { cart, product, variant, properties } })
    );
    showSuccess(product, variant, trigger, properties);
  }

  async function refreshCartCount() {
    const cartUrl = `${(window.routes?.cart_url || '/cart').replace(/\/$/, '')}.js`;
    const response = await fetch(cartUrl, {
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json',
      },
    });
    if (!response.ok) return null;
    const cart = await response.json();

    document.querySelectorAll('.llagox-cart-badge').forEach((badge) => {
      badge.textContent = cart.item_count;
      badge.hidden = false;
    });
    document.querySelectorAll('#cart-icon-bubble .cart-count-bubble span[aria-hidden="true"]').forEach((badge) => {
      badge.textContent = cart.item_count;
    });
    return cart;
  }

  function showSuccess(product, variant, trigger, properties = {}) {
    showView(successView);
    const image = successView.querySelector('[data-llagox-success-image]');
    image.src = trigger?.dataset.llagoxProductImage || imageUrl(variant.featured_image) || imageUrl(product.featured_image) || '';
    image.alt = product.title || trigger?.dataset.llagoxProductTitle || '';
    successView.querySelector('[data-llagox-success-title]').textContent = product.title || trigger?.dataset.llagoxProductTitle || '';
    successView.querySelector('[data-llagox-success-price]').textContent = formatPrice(variant.price);

    const details = successView.querySelector('[data-llagox-success-details]');
    details.replaceChildren();
    const names = optionNames(product);
    variantOptions(variant).forEach((value, index) => {
      const optName = names[index] || 'Seçenek';
      if (!value || value === 'Default Title') return;
      const term = document.createElement('dt');
      term.textContent = `${optName}:`;
      const description = document.createElement('dd');
      description.textContent = value;
      details.append(term, description);
    });
    Object.entries(properties).forEach(([name, value]) => {
      const lowerName = String(name || '').toLowerCase().trim();
      if (
        !name ||
        value == null ||
        value === '' ||
        lowerName.startsWith('_') ||
        lowerName.includes('image') ||
        lowerName.includes('görsel') ||
        lowerName.includes('img') ||
        lowerName.includes('url')
      )
        return;
      const term = document.createElement('dt');
      term.textContent = `${name}:`;
      const description = document.createElement('dd');
      description.textContent = value;
      details.append(term, description);
    });
    const quantityTerm = document.createElement('dt');
    quantityTerm.textContent = 'Adet:';
    const quantityDescription = document.createElement('dd');
    quantityDescription.textContent = '1';
    details.append(quantityTerm, quantityDescription);

    openModal();
  }

  function showError(message) {
    showView(errorView);
    errorView.querySelector('[data-llagox-error-message]').textContent = message;
    openModal();
  }

  function showView(view) {
    [pickerView, successView, errorView].forEach((item) => {
      item.hidden = item !== view;
    });
  }

  function openModal() {
    modal.hidden = false;
    document.body.classList.add('llagox-modal-open');
    requestAnimationFrame(() => {
      const target = modal.querySelector('.llagox-quick-cart__view:not([hidden]) button:not([disabled])') || dialog;
      target.focus();
    });
  }

  function closeModal() {
    if (!modal || modal.hidden) return;
    modal.hidden = true;
    document.body.classList.remove('llagox-modal-open');
    state = {};
    activeTrigger?.focus();
  }

  function setTriggerLoading(trigger, loading) {
    const label = trigger.querySelector('[data-llagox-button-label]');
    if (!label) return;
    if (loading) {
      trigger.dataset.llagoxOriginalLabel = label.textContent;
      trigger.disabled = true;
      label.textContent = 'YÜKLENİYOR…';
    } else {
      trigger.disabled = false;
      label.textContent = trigger.dataset.llagoxOriginalLabel || 'SEPETE EKLE';
    }
  }

  function imageUrl(image) {
    if (!image) return '';
    return typeof image === 'string' ? image : image.src || '';
  }

  function formatPrice(cents) {
    const amount = Number(cents);
    if (!Number.isFinite(amount)) return '';
    const currency = window.Shopify?.currency?.active || 'TRY';
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency }).format(amount / 100);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
